/**
 * Organization and site-administration API client.
 *
 * Note `affiliate` returns `grants_authority`. The server says so explicitly rather than leaving
 * the client to assume, because "I joined my diocese, so surely I can see its liturgies" is exactly
 * the wrong inference and the API should refuse to imply it.
 */

import { request } from '../../api/client';

export type OrganizationKind =
  | 'diocese'
  | 'archdiocese'
  | 'eparchy'
  | 'religious_community'
  | 'seminary'
  | 'parish'
  | 'shrine'
  | 'cathedral'
  | 'basilica'
  | 'other';

export interface Organization {
  id: string;
  name: string;
  kind: OrganizationKind;
  parent_organization_id: string | null;
  parent_name: string | null;
  profile: Record<string, unknown>;
  status: string;
  /** Shown so somebody who searched a variant understands why they found this row. */
  aliases: string[];
  retired_at: string | null;
}

export interface OrganizationRequest {
  id: string;
  requested_name: string;
  requested_kind: string;
  detail?: string | null;
  requester?: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  decision_note: string | null;
  organization_id?: string | null;
  created_at: string;
  /** Answered before the reviewer asks. The whole reason the registry is curated. */
  possible_duplicate?: { id: string; name: string } | null;
}

export interface AuditEntry {
  id: string;
  actor: string;
  action: string;
  resource_kind: string;
  resource_id: string | null;
  resource_label: string | null;
  outcome: string;
  reason: string | null;
  occurred_at: string;
}

export const ORGANIZATION_KIND_LABELS: Record<OrganizationKind, string> = {
  diocese: 'Diocese',
  archdiocese: 'Archdiocese',
  eparchy: 'Eparchy',
  religious_community: 'Religious community',
  seminary: 'Seminary',
  parish: 'Parish',
  shrine: 'Shrine',
  cathedral: 'Cathedral',
  basilica: 'Basilica',
  other: 'Other',
};

// ── the registry ────────────────────────────────────────────────────────────

export function fetchOrganizations(params: {
  q?: string;
  kind?: string;
}): Promise<{ data: Organization[]; kinds: OrganizationKind[] }> {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.kind) query.set('kind', params.kind);

  const suffix = query.toString();
  return request(`/organizations${suffix ? `?${suffix}` : ''}`);
}

export function checkName(
  name: string,
): Promise<{ existing: { id: string; name: string } | null; available: boolean }> {
  return request(`/organizations/check-name?name=${encodeURIComponent(name)}`);
}

export function requestOrganization(input: {
  name: string;
  kind: OrganizationKind;
  detail: string;
}): Promise<{ id: string; status: string }> {
  return request('/organization-requests', { method: 'POST', body: JSON.stringify(input) });
}

export function fetchMyRequests(): Promise<{ data: OrganizationRequest[] }> {
  return request('/organization-requests');
}

export function withdrawRequest(id: string): Promise<void> {
  return request(`/organization-requests/${id}/withdraw`, { method: 'POST' });
}

// ── affiliation ─────────────────────────────────────────────────────────────

export function fetchAffiliations(): Promise<{ data: Organization[] }> {
  return request('/me/affiliations');
}

export function affiliate(id: string): Promise<{ grants_authority: boolean }> {
  return request(`/organizations/${id}/affiliate`, { method: 'POST' });
}

export function unaffiliate(id: string): Promise<void> {
  return request(`/organizations/${id}/affiliate`, { method: 'DELETE' });
}

// ── site administration ─────────────────────────────────────────────────────

export function fetchSiteOverview(): Promise<{
  pending_organization_requests: number;
  recent_audit: AuditEntry[];
}> {
  return request('/site');
}

export function fetchReviewQueue(all = false): Promise<{ data: OrganizationRequest[] }> {
  return request(`/site/organization-requests${all ? '?status=all' : ''}`);
}

export function approveRequest(
  id: string,
  input: { name: string; kind: string; note?: string },
): Promise<{ id: string; name: string }> {
  return request(`/site/organization-requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function rejectRequest(id: string, note: string): Promise<{ id: string }> {
  return request(`/site/organization-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

export function createOrganizationDirectly(input: {
  name: string;
  kind: OrganizationKind;
  parent_organization_id?: string | null;
}): Promise<{ id: string; name: string; kind: string }> {
  return request('/site/organizations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function retireOrganization(id: string): Promise<{ id: string; status: string }> {
  return request(`/site/organizations/${id}/retire`, {
    method: 'POST',
  });
}

export function fetchAudit(params: { action?: string; resource_kind?: string } = {}): Promise<{
  data: AuditEntry[];
}> {
  const query = new URLSearchParams();
  if (params.action) query.set('action', params.action);
  if (params.resource_kind) query.set('resource_kind', params.resource_kind);

  const suffix = query.toString();
  return request(`/site/audit${suffix ? `?${suffix}` : ''}`);
}

/** Governance actions in words, so an audit page reads as English rather than as identifiers. */

// ── template curation ───────────────────────────────────────────────────────

/**
 * Designation changes **discoverability, never authority**.
 *
 * Marking a template Official does not make it correct, does not make the site administrator its
 * owner, and does not let anybody edit it who could not before. It says: this is the one to reach
 * for. Keeping that distinction visible is most of the curation screen's job.
 */
export type DesignationKind = 'official' | 'featured';

export function designateTemplate(id: string, kind: DesignationKind): Promise<unknown> {
  return request(`/site/templates/${id}/designate`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
}

export function withdrawDesignation(id: string, kind: DesignationKind): Promise<unknown> {
  return request(`/site/templates/${id}/withdraw-designation`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
}

export const DESIGNATION_COPY: Record<DesignationKind, { label: string; note: string }> = {
  official: {
    label: 'Official',
    note: 'The authoritative starting point for this kind of celebration.',
  },
  featured: {
    label: 'Featured',
    note: 'Worth finding. A good example, without claiming authority.',
  },
};

// ── organization administration (screen 22) ─────────────────────────────────

export interface OrganizationAdministrator {
  account_id: string;
  display_name: string;
  assigned_at: string;
}

export interface OrganizationDetail extends Organization {
  administrators: OrganizationAdministrator[];
  defaults: Record<string, unknown>;
  /**
   * Stated by the server, never inferred from affiliation by the client.
   *
   * The two are unrelated: affiliating with a diocese tells the application where somebody serves
   * and grants nothing. Administration is assigned deliberately, one person at a time.
   */
  may_administer: boolean;
}

export function fetchOrganization(id: string): Promise<{ data: OrganizationDetail }> {
  return request(`/organizations/${id}`);
}

export function saveProfile(id: string, profile: Record<string, unknown>): Promise<unknown> {
  return request(`/organizations/${id}/profile`, {
    method: 'PUT',
    body: JSON.stringify(profile),
  });
}

/**
 * Defaults are **overridable starting points**, never constraints.
 *
 * The server restates that on every write, and the screen repeats it, because it is the distinction
 * people get wrong: only a Template Constraint can block a Finalization.
 */
export function saveDefaults(
  id: string,
  defaults: Record<string, unknown>,
): Promise<{ data: Record<string, unknown>; note: string }> {
  return request(`/organizations/${id}/defaults`, {
    method: 'PUT',
    body: JSON.stringify(defaults),
  });
}

export function assignAdministrator(
  id: string,
  identifier: string,
): Promise<{ organization_id: string; account_id: string; display_name?: string }> {
  return request(`/organizations/${id}/administrators`, {
    method: 'POST',
    body: JSON.stringify({ identifier }),
  });
}

export function assignSiteOrganizationAdmin(
  id: string,
  identifier: string,
): Promise<{ organization_id: string; account_id: string; display_name?: string }> {
  return request(`/site/organizations/${id}/administrators`, {
    method: 'POST',
    body: JSON.stringify({ identifier }),
  });
}

export function revokeAdministrator(id: string, accountId: string): Promise<unknown> {
  return request(`/organizations/${id}/administrators/${accountId}`, { method: 'DELETE' });
}

export function fetchSiteAccounts(query?: string): Promise<{
  data: { id: string; email: string; display_name: string; status: string; created_at: string }[];
}> {
  const q = query ? `?q=${encodeURIComponent(query)}` : '';
  return request(`/site/accounts${q}`);
}

export function disableAccount(
  id: string,
  reason: string,
): Promise<{ id: string; status: string; ownership_preserved: boolean }> {
  return request(`/site/accounts/${id}/disable`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function enableAccount(id: string): Promise<{ id: string; status: string }> {
  return request(`/site/accounts/${id}/enable`, {
    method: 'POST',
  });
}
